/**
 * Phase 1 — duel-invite popup. Registers a `duel_request` handler on the message
 * bus and shows a "Play now / Later" dialog when a friend challenges you.
 * Suppressed in test mode (mid-solve / live duel) — queued and shown on exit.
 * "Play now" routes to the challenge's actionUrl (the code auto-join flow).
 *
 * See docs/FeatureDesigns/duels-invite-smoothing-plan.md (Phase 1).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { ServerMessage } from '@shared-ui/api';
import { registerMessageHandler } from '@shared-ui/messages';
import { isTestModePath } from './testMode';

interface DuelReq { id: string; title: string; body: string; actionUrl?: string }

export function DuelRequestPopup() {
  const [queue, setQueue] = useState<DuelReq[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const off = registerMessageHandler('duel_request', (m: ServerMessage) => {
      const p = (m.payload ?? {}) as { title?: string; body?: string; actionUrl?: string };
      setQueue(q => q.some(x => x.id === m.id)
        ? q
        : [...q, { id: m.id, title: p.title || 'Duel challenge', body: p.body || '', actionUrl: p.actionUrl }]);
    });
    return off;
  }, []);

  // Hold while mid-solve; the next render after leaving test mode shows it.
  const current = isTestModePath(location.pathname) ? undefined : queue[0];
  if (!current) return null;

  // "Later"/backdrop clears the WHOLE queue (a single tap dismisses all pending
  // challenges) — otherwise a backlog of undelivered invites would re-appear one
  // at a time and feel un-dismissable. Received messages are already acked on
  // receipt, so they won't re-deliver. `next` advances to the following invite
  // instead of clearing everything.
  const dismissAll = () => setQueue([]);
  const next = () => setQueue(q => q.slice(1));
  const playNow = () => {
    const url = current.actionUrl;
    next();
    if (url) navigate(url.startsWith('/') ? url : `/${url}`);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={dismissAll}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #1c1f33)', borderRadius: 18, padding: 24, width: '100%', maxWidth: 360, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
      >
        <div style={{ fontSize: 34 }}>⚡</div>
        <h3 style={{ margin: '10px 0 4px', fontSize: 18 }}>{current.title}</h3>
        {current.body && <p style={{ margin: '0 0 6px', fontSize: 14, color: 'var(--text-secondary, #5a5f72)' }}>{current.body}</p>}
        {queue.length > 1 && <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-tertiary, #9499a8)' }}>+{queue.length - 1} more · Later dismisses all</p>}
        <div style={{ display: 'flex', gap: 10, marginTop: current.body || queue.length > 1 ? 14 : 0 }}>
          <button
            onClick={playNow}
            style={{ flex: 1, border: 'none', borderRadius: 12, padding: '13px', fontSize: 15, fontWeight: 800, color: '#fff', background: 'linear-gradient(135deg, #4f46e5, #6366f1)', cursor: 'pointer' }}
          >
            ⚡ Play now
          </button>
          <button
            onClick={dismissAll}
            style={{ flex: '0 0 auto', border: '1.5px solid var(--border-color, #e7e9f0)', borderRadius: 12, padding: '13px 18px', fontSize: 15, fontWeight: 700, background: 'transparent', color: 'var(--text-secondary, #5a5f72)', cursor: 'pointer' }}
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
